"use client";

import { useState, useEffect, type ReactNode } from "react";

/* DeferIdle — renders its children only after the browser goes idle (or a
 * timeout fallback), so non-critical, non-above-the-fold widgets don't hydrate
 * during the initial load and don't compete for main-thread time while the page
 * is still painting. Use ONLY for floating / always-on UI that renders nothing
 * above the fold (exit-intent popups, live-activity toasts, sticky CTAs, the
 * mascot) — deferring their mount has no visual or SEO cost, and takes their
 * hydration off the critical path on every page. */
type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

export function DeferIdle({
  children,
  timeout = 2500,
}: {
  children: ReactNode;
  timeout?: number;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const w = window as IdleWindow;
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(() => setReady(true), { timeout });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(() => setReady(true), timeout);
    return () => clearTimeout(t);
  }, [timeout]);

  return ready ? <>{children}</> : null;
}
